import { useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { cn, hasModule, pm } from 'lib/helpers/utility';

import { modulesState, permissions as permissionsState } from 'components/state';

import List from './List';

/**
 * @param {object} props
 * @param {Array} props.items Groups of `{ title, permission?, module?, items: [] }`.
 *   A child is `{ key, label, icon?, permission?, module?, hidden?, content }`.
 * @param {boolean} [props.keepMounted] Renders every visible panel and hides the
 *   inactive ones. Set it when panels hold unsaved edits that must survive a tab
 *   switch — the default unmounts them.
 */
const ModalSidebar = ({
  items,
  className,
  listClassName,
  contentClassName,
  activeKey,
  onActiveChange,
  keepMounted = false
}) => {
  const perms = useRecoilValue(permissionsState);
  const practiceModules = useRecoilValue(modulesState);
  const [uncontrolledKey, setUncontrolledKey] = useState(null);

  const checkPerm = useCallback((p) => !p || pm(perms, p), [perms]);
  const checkModule = useCallback((m) => !m || hasModule(practiceModules, m), [practiceModules]);

  const computedDefault = useMemo(() => {
    const outer = [];
    for (const group of items || []) {
      if (!checkPerm(group?.permission) || !checkModule(group?.module)) continue;
      for (const child of group?.items || []) {
        if (
          child?.key &&
          !child.hidden &&
          checkPerm(child?.permission) &&
          checkModule(child?.module)
        )
          outer.push(child);
      }
    }
    return outer;
  }, [items, checkPerm, checkModule]);

  const isControlled = activeKey != null;
  const requestedKey = isControlled ? activeKey : uncontrolledKey;

  const activeItem = useMemo(
    () =>
      computedDefault.find((ch) => String(ch?.key) === String(requestedKey)) ?? computedDefault[0],
    [computedDefault, requestedKey]
  );

  const changeHandler = useCallback(
    (item) => {
      if (!item?.key) return;
      const nextKey = String(item.key);
      if (nextKey === String(activeItem?.key)) return;
      if (!isControlled) setUncontrolledKey(nextKey);
      onActiveChange?.(nextKey);
    },
    [activeItem, isControlled, onActiveChange]
  );

  const resolveContent = (item) =>
    typeof item?.content === 'function' ? item?.content() : item?.content;

  return (
    <div
      className={cn('flex h-full w-full shrink-0 bg-white transition-all xs:flex-col', className)}>
      <aside
        className={cn(
          'flex w-52 flex-col items-start justify-between overflow-y-auto border-0 border-r border-solid border-neutral-200',
          'xs:w-full xs:shrink-0 xs:overflow-visible xs:border-b xs:border-r-0',
          listClassName
        )}>
        <List items={items} onChange={changeHandler} activeItem={activeItem} />
      </aside>
      <div className={cn('flex flex-1 flex-col', contentClassName)}>
        {keepMounted
          ? computedDefault.map((item) => (
              <div
                key={item.key}
                className={cn(
                  'flex min-h-0 flex-1 flex-col',
                  String(item.key) !== String(activeItem?.key) && 'hidden'
                )}>
                {resolveContent(item)}
              </div>
            ))
          : resolveContent(activeItem)}
      </div>
    </div>
  );
};

export default ModalSidebar;
